import { useState } from 'react';
import { ThumbsUp, ThumbsDown, Trash2, Play, Send, Trophy, XCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import type { Opportunity, OpportunityStatus } from '../../types/internal-opportunity.types';
import { STATUS_CONFIG } from '../../types/internal-opportunity.types';

interface OpportunityActionsProps {
  opportunity: Opportunity;
  onDecision: (decision: 'go' | 'no_go', reason?: string) => void;
  onStatusChange: (status: OpportunityStatus) => void;
  onDelete: () => void;
  onRefresh?: () => void;
  isLoading?: boolean;
  isRefreshing?: boolean;
}

const STATUS_TRANSITIONS: Record<OpportunityStatus, OpportunityStatus[]> = {
  new: ['qualified', 'abandoned'],
  qualified: ['in_progress', 'abandoned'],
  in_progress: ['submitted', 'abandoned'],
  submitted: ['won', 'lost'],
  won: [],
  lost: [],
  abandoned: [],
};

export function OpportunityActions({
  opportunity,
  onDecision,
  onStatusChange,
  onDelete,
  onRefresh,
  isLoading,
  isRefreshing,
}: OpportunityActionsProps) {
  const [showDecisionDialog, setShowDecisionDialog] = useState<'go' | 'no_go' | null>(null);
  const [decisionReason, setDecisionReason] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const availableTransitions = STATUS_TRANSITIONS[opportunity.status] || [];
  const isTerminal = availableTransitions.length === 0;
  const canMakeDecision =
    opportunity.status === 'new' && !opportunity.decision;

  const handleDecisionConfirm = () => {
    if (showDecisionDialog) {
      onDecision(showDecisionDialog, decisionReason || undefined);
      setShowDecisionDialog(null);
      setDecisionReason('');
    }
  };

  const getStatusIcon = (status: OpportunityStatus) => {
    switch (status) {
      case 'qualified':
        return <Play className="h-4 w-4" />;
      case 'in_progress':
        return <Play className="h-4 w-4" />;
      case 'submitted':
        return <Send className="h-4 w-4" />;
      case 'won':
        return <Trophy className="h-4 w-4" />;
      case 'lost':
        return <XCircle className="h-4 w-4" />;
      case 'abandoned':
        return <XCircle className="h-4 w-4" />;
      default:
        return null;
    }
  };

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        {/* Décision GO/NO-GO */}
        {canMakeDecision && (
          <>
            <Button
              variant="default"
              className="bg-green-600 hover:bg-green-700"
              onClick={() => setShowDecisionDialog('go')}
              disabled={isLoading}
            >
              <ThumbsUp className="h-4 w-4 mr-2" />
              Décision GO
            </Button>
            <Button
              variant="destructive"
              onClick={() => setShowDecisionDialog('no_go')}
              disabled={isLoading}
            >
              <ThumbsDown className="h-4 w-4 mr-2" />
              Décision NO-GO
            </Button>
          </>
        )}

        {/* Changement de statut */}
        {!isTerminal && availableTransitions.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={isLoading}>
                Changer le statut
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {availableTransitions.map((status) => {
                const config = STATUS_CONFIG[status];
                return (
                  <DropdownMenuItem
                    key={status}
                    onClick={() => onStatusChange(status)}
                    className={status === 'abandoned' ? 'text-destructive' : ''}
                  >
                    {getStatusIcon(status)}
                    <span className="ml-2">{config.label}</span>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Rafraîchissement depuis la source */}
        {opportunity.externalId && opportunity.importSource && onRefresh && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={isLoading || isRefreshing}
            title="Rafraîchir les données depuis BOAMP"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        )}

        {/* Suppression */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setShowDeleteDialog(true)}
          disabled={isLoading}
          className="text-destructive hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Dialog de décision */}
      <Dialog
        open={showDecisionDialog !== null}
        onOpenChange={() => {
          setShowDecisionDialog(null);
          setDecisionReason('');
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {showDecisionDialog === 'go'
                ? 'Confirmer la décision GO'
                : 'Confirmer la décision NO-GO'}
            </DialogTitle>
            <DialogDescription>
              {showDecisionDialog === 'go'
                ? 'Cette opportunité sera marquée comme prioritaire et passera en statut "En cours".'
                : 'Cette opportunité sera abandonnée et ne sera plus suivie.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reason">Raison (optionnel)</Label>
              <Textarea
                id="reason"
                placeholder="Expliquez votre décision..."
                value={decisionReason}
                onChange={(e) => setDecisionReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDecisionDialog(null);
                setDecisionReason('');
              }}
            >
              Annuler
            </Button>
            <Button
              variant={showDecisionDialog === 'go' ? 'default' : 'destructive'}
              onClick={handleDecisionConfirm}
              className={showDecisionDialog === 'go' ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de suppression */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'opportunité ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. L'opportunité sera définitivement
              supprimée de votre liste.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
